import { useEffect, useRef, useState } from "react";
import { copyTextToClipboard } from "../student/cohortLessonStudentLink";
import { formatDuePresentation } from "./formatDueTimeOfDay";
import { datetimeLocalToIso, isoToDatetimeLocal } from "./datetimeLocal";

export interface AssignmentDueDateModalProps {
  readonly open: boolean;
  readonly lessonTitle: string;
  readonly itemKind?: "lesson" | "quiz";
  readonly dueAt: string | null;
  readonly assignMode?: boolean;
  readonly studentLinkUrl?: string | null;
  readonly disabled?: boolean;
  readonly saving?: boolean;
  readonly onClose: () => void;
  readonly onCommit: (dueAt: string | null) => void | Promise<void>;
  readonly onCopyToast?: (message: string, kind: "success" | "error") => void;
}

export function AssignmentDueDateModal({
  open,
  lessonTitle,
  itemKind = "lesson",
  dueAt,
  assignMode = false,
  studentLinkUrl,
  disabled,
  saving,
  onClose,
  onCommit,
  onCopyToast,
}: AssignmentDueDateModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState(() => isoToDatetimeLocal(dueAt));
  const initialRef = useRef(isoToDatetimeLocal(dueAt));
  const [phase, setPhase] = useState<"edit" | "assigned">("edit");

  useEffect(() => {
    if (!open) {
      setPhase("edit");
    }
  }, [open]);

  useEffect(() => {
    if (!open || phase === "assigned") return;
    const local = isoToDatetimeLocal(dueAt);
    setDraft(local);
    initialRef.current = local;
  }, [open, dueAt, phase]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  const previewIso = draft.trim() ? datetimeLocalToIso(draft) : null;
  const presentation = formatDuePresentation(previewIso);
  const period = presentation?.period ?? "afternoon";

  function draftIso(): string | null {
    if (!draft.trim()) return null;
    return datetimeLocalToIso(draft);
  }

  function initialIso(): string | null {
    if (!initialRef.current.trim()) return null;
    return datetimeLocalToIso(initialRef.current);
  }

  async function handleDone() {
    if (disabled || saving) return;
    const nextIso = draftIso();
    if (draft.trim() && nextIso === null) return;

    if (assignMode) {
      try {
        await onCommit(nextIso);
        initialRef.current = draft;
        setPhase("assigned");
      } catch {
        /* parent surfaces save errors */
      }
      return;
    }

    const prevIso = initialIso();
    try {
      if (nextIso !== prevIso) {
        await onCommit(nextIso);
        initialRef.current = draft;
      }
      onClose();
    } catch {
      /* parent surfaces save errors */
    }
  }

  async function handleCopyLink() {
    if (!studentLinkUrl) return;
    const ok = await copyTextToClipboard(studentLinkUrl);
    if (ok) {
      onCopyToast?.("Student link copied", "success");
    } else {
      onCopyToast?.("Could not copy link", "error");
    }
  }

  const showAssignedSuccess = assignMode && phase === "assigned";

  return (
    <dialog
      ref={dialogRef}
      className="teacher-modal assignment-due-modal"
      onClose={onClose}
      onClick={(ev) => {
        if (ev.target === dialogRef.current && !showAssignedSuccess) onClose();
      }}
    >
      <div className="teacher-modal-card assignment-due-modal-card" role="document">
        {showAssignedSuccess ? (
          <>
            <h2 className="teacher-modal-title">Lesson assigned</h2>
            <p className="muted small" style={{ marginTop: 0 }}>
              {lessonTitle}
            </p>
            <p className="assignment-due-assigned-lede">
              Share this link in Google Classroom or anywhere students should open the lesson. Every student in this
              cohort can use the same link.
            </p>
            <div className="assignment-due-link-preview" aria-label="Student lesson link">
              <code className="assignment-due-link-preview-code">{studentLinkUrl ?? ""}</code>
            </div>
            <div className="teacher-modal-actions">
              <button type="button" className="btn secondary" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={!studentLinkUrl}
                onClick={() => void handleCopyLink()}
              >
                Copy student link
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="teacher-modal-title">
              {assignMode ? (itemKind === "quiz" ? "Assign quiz" : "Assign lesson") : "Due date"}
            </h2>
            <p className="muted small" style={{ marginTop: 0 }}>
              {lessonTitle}
            </p>
            {assignMode ? (
              <p className="muted small assignment-due-assign-hint">
                Set an optional due date, then click Done to open the lesson for students.
              </p>
            ) : null}

            <div className={`assignment-due-visual assignment-due-visual--${period}`} aria-live="polite">
              {presentation ? (
                <>
                  <p className="assignment-due-visual-headline">{presentation.headline}</p>
                  <p className="assignment-due-visual-detail">{presentation.detail}</p>
                </>
              ) : (
                <>
                  <p className="assignment-due-visual-headline">No due date</p>
                  <p className="assignment-due-visual-detail">Students won't see a deadline for this lesson.</p>
                </>
              )}
            </div>

            <label className="field">
              <span className="field-label">Date &amp; time</span>
              <input
                className="input"
                type="datetime-local"
                value={draft}
                disabled={disabled || saving}
                onChange={(ev) => setDraft(ev.target.value)}
              />
            </label>

            <div className="teacher-modal-actions">
              <button
                type="button"
                className="btn secondary"
                disabled={disabled || saving}
                onClick={() => setDraft("")}
              >
                Clear due date
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={disabled || saving}
                onClick={() => void handleDone()}
              >
                {saving ? "Saving…" : "Done"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  );
}
